import type { UserBiData } from './hooks/useUserBi/useUserBi.interface.js';
import type { PanelType, ViewType } from './userbi.js';

export interface RouteTelemetry {
  panelType: PanelType;
  view: ViewType;
  data?: UserBiData;
}

export interface RouteTelemetryContext {
  location: {
    pathname: string;
    search: string;
  };
  params: Readonly<Record<string, string | undefined>>;
}

export type RouteTelemetryResolver = (
  context: RouteTelemetryContext,
) => RouteTelemetry | null;

export interface RouteTelemetryHandle {
  telemetry: RouteTelemetry | RouteTelemetryResolver;
}

export interface RouteTelemetryMatch {
  handle: unknown;
  params: Readonly<Record<string, string | undefined>>;
}

export function telemetryHandle(
  panelType: PanelType,
  view: ViewType,
  data?: UserBiData,
): RouteTelemetryHandle {
  return { telemetry: { panelType, view, data } };
}

export function dynamicTelemetryHandle(
  telemetry: RouteTelemetryResolver,
): RouteTelemetryHandle {
  return { telemetry };
}

function getTelemetryDefinition(
  handle: unknown,
): RouteTelemetryHandle['telemetry'] | null {
  if (
    typeof handle !== 'object' ||
    handle === null ||
    !('telemetry' in handle)
  ) {
    return null;
  }
  return (handle as RouteTelemetryHandle).telemetry;
}

export function resolveRouteTelemetry(
  matches: RouteTelemetryMatch[],
  location: RouteTelemetryContext['location'],
): RouteTelemetry | null {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const match = matches[index];
    if (match === undefined) continue;
    const definition = getTelemetryDefinition(match.handle);
    if (definition === null) continue;
    return typeof definition === 'function'
      ? definition({ location, params: match.params })
      : definition;
  }
  return null;
}

export function getRouteTelemetryKey(route: RouteTelemetry): string {
  const data = route.data
    ? Object.entries(route.data)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, value]) => `${key}=${String(value)}`)
        .join('&')
    : '';
  return `${route.panelType}:${route.view}:${data}`;
}
